import { useEffect, useRef } from 'react';

export interface RequestDraftFields {
  title: string;
  description: string;
  projectId: string;
  phaseId: string;
}

const DEBOUNCE_MS = 800;

function storageKey(userId: string, requestId: string | null): string {
  return `webqn_feature_request_draft_${userId}_${requestId ?? 'new'}`;
}

/** true nếu nháp có ít nhất 1 trường khác rỗng — nháp toàn rỗng không đáng lưu/hỏi khôi phục. */
function isMeaningful(fields: RequestDraftFields): boolean {
  return Object.values(fields).some((v) => v.trim() !== '');
}

/**
 * Đọc nháp đã lưu (nếu có) của 1 form — gọi 1 lần khi dialog mở, KHÔNG tự
 * điền; nơi gọi tự quyết định hỏi user trước (ConfirmDialog) rồi mới áp dụng.
 */
export function readRequestDraft(userId: string | null, requestId: string | null): RequestDraftFields | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(storageKey(userId, requestId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RequestDraftFields>;
    const fields: RequestDraftFields = {
      title: parsed.title ?? '',
      description: parsed.description ?? '',
      projectId: parsed.projectId ?? '',
      phaseId: parsed.phaseId ?? '',
    };
    return isMeaningful(fields) ? fields : null;
  } catch {
    return null;
  }
}

export function clearRequestDraft(userId: string | null, requestId: string | null): void {
  if (!userId) return;
  localStorage.removeItem(storageKey(userId, requestId));
}

/**
 * Tự lưu (debounce) title/description/project/phase vào localStorage trong
 * lúc user gõ — khôi phục được nếu lỡ đóng tab/reload giữa chừng. File đính
 * kèm (File[]) KHÔNG lưu được vì không serialize qua JSON (giống giới hạn đã
 * chấp nhận ở useChatDraft của chatbot) — chỉ mất phần đó, phải chọn lại.
 */
export function useAutosaveRequestDraft(
  userId: string | null,
  requestId: string | null,
  active: boolean,
  fields: RequestDraftFields,
  onSaved?: () => void,
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!active || !userId) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (isMeaningful(fields)) {
        localStorage.setItem(storageKey(userId, requestId), JSON.stringify(fields));
      } else {
        localStorage.removeItem(storageKey(userId, requestId));
      }
      // Callback để UI hiển thị "Đã lưu nháp"
      onSaved?.();
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, userId, requestId, fields.title, fields.description, fields.projectId, fields.phaseId, onSaved]);
}
